import React from 'react'
import { EventSourcePolyfill } from 'event-source-polyfill'
import { Constants } from '@/assets'
import { _getItem, TokenKeys } from '@/modules/Auth/LocalStorageHelper'

type UseMercureOptions = {
  completeTopicPath?: boolean
  onBeforeReconnect?: () => Promise<void> | void
  pauseOnHidden?: boolean
  extraHeaders?: Record<string, string>
  topics?: string[] | null
}

const useMercureES = (
  topicPath: string | null,
  onMessage: (data: unknown) => void | Promise<void>,
  onError?: (error?: Error) => void | Promise<void>,
  onOpen?: () => void | Promise<void>,
  { completeTopicPath, onBeforeReconnect, pauseOnHidden = false, extraHeaders, topics }: UseMercureOptions = {},
): void => {
  const esRef = React.useRef<EventSourcePolyfill | null>(null)
  const closedRef = React.useRef(false)
  const onlineRef = React.useRef(typeof navigator === 'undefined' ? true : navigator.onLine)
  const reconnectTimerRef = React.useRef<number | null>(null)
  const reconnectingRef = React.useRef(false)

  const mercureToken = _getItem(TokenKeys.Mercure)
  const mercureUrl = _getItem(TokenKeys.MercureUrl)

  const topic = React.useMemo(() => {
    if (!topicPath) return ''
    return completeTopicPath ? topicPath : new URL(topicPath, Constants.mercureTopicUrl).toString()
  }, [topicPath, completeTopicPath])

  const resolvedTopics = React.useMemo(() => {
    const list = (topics && topics.length ? topics : [topic]).filter(Boolean) as string[]
    return Array.from(new Set(list))
  }, [topic, topics])

  const cleanupES = React.useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    if (esRef.current) {
      esRef.current.close()
      esRef.current = null
    }

    reconnectingRef.current = false
  }, [])

  const subscribe = React.useCallback(async () => {
    if (closedRef.current) return
    if (!mercureToken || !mercureUrl || resolvedTopics.length === 0) return
    if (!onlineRef.current) return
    if (pauseOnHidden && typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    if (esRef.current) return

    try {
      if (onBeforeReconnect) {
        await onBeforeReconnect()
      }
    } catch {
      // ignore
    }

    try {
      const url = new URL(mercureUrl)

      for (const t of resolvedTopics) {
        url.searchParams.append('topic', t)
      }

      const es = new EventSourcePolyfill(url.toString(), {
        headers: {
          Authorization: `Bearer ${mercureToken}`,
          Accept: 'text/event-stream',
          ...(extraHeaders || {}),
        },
        withCredentials: false,
        heartbeatTimeout: 45000,
      })

      es.onopen = () => {
        reconnectingRef.current = false
        onOpen?.()
      }

      es.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data)
          onMessage(data)
        } catch (err: any) {
          onError?.(err instanceof Error ? err : new Error(String(err)))
        }
      }

      es.onerror = (evt: any) => {
        onError?.(new Error(evt?.message || 'SSE error'))

        if (closedRef.current) return
        if (!onlineRef.current) return
        if (pauseOnHidden && typeof document !== 'undefined' && document.visibilityState === 'hidden') return
        if (reconnectingRef.current) return

        reconnectingRef.current = true

        if (esRef.current) {
          esRef.current.close()
          esRef.current = null
        }

        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null
          reconnectingRef.current = false
          subscribe()
        }, 1000)
      }

      esRef.current = es
    } catch (err: any) {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    }
  }, [
    mercureToken,
    mercureUrl,
    resolvedTopics,
    pauseOnHidden,
    extraHeaders,
    onBeforeReconnect,
    onMessage,
    onError,
    onOpen,
  ])

  React.useEffect(() => {
    if (!resolvedTopics.length) return

    closedRef.current = false
    subscribe()

    return () => {
      closedRef.current = true
      cleanupES()
    }
  }, [resolvedTopics, subscribe, cleanupES])

  React.useEffect(() => {
    const onOnline = () => {
      onlineRef.current = true
      subscribe()
    }

    const onOffline = () => {
      onlineRef.current = false
      cleanupES()
    }

    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [subscribe, cleanupES])

  React.useEffect(() => {
    if (!pauseOnHidden) return

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        subscribe()
      } else {
        cleanupES()
      }
    }

    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [pauseOnHidden, subscribe, cleanupES])
}

export default useMercureES
